/**
 * The roster's unpublished-edit count, per schedule, shared between the
 * roster page and the lead's next-steps panel (#1921).
 *
 * The number behind `Publish updates (N)` is the roster read's
 * `unpublished_count`, which `<RosterHeatmap/>` reports up through
 * `onUnpublishedCountChange`. `<RosterPanel/>` used to keep it in component
 * state, so nothing outside the page could read it — and the panel's
 * *"1 unpublished edit · the team has not seen it"* row needs exactly that
 * number, including the page's own zero right after a publish (before the
 * roster read refetches). So the count lives here instead: the heatmap's
 * callback writes it, the Publish button and `useNextSteps` read it. One
 * number, one writer, no second computation.
 *
 * A module-level `useSyncExternalStore` store keyed by cycle id. `null`
 * means "no roster page has reported for this schedule yet" — the panel
 * then falls back to the same `unpublished_count` field on the roster read
 * it already holds.
 */
import { useCallback, useSyncExternalStore } from "react";

const counts = new Map<string, number>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Record the count for one schedule (no-op when unchanged). */
export function setUnpublishedEdits(cycleId: string, count: number): void {
  if (counts.get(cycleId) === count) return;
  counts.set(cycleId, count);
  emit();
}

/** The count last reported for this schedule, or `null` if none has been. */
export function getUnpublishedEdits(cycleId: string | undefined): number | null {
  if (!cycleId) return null;
  return counts.get(cycleId) ?? null;
}

/** Reactive read of `getUnpublishedEdits`. */
export function useUnpublishedEdits(cycleId: string | undefined): number | null {
  return useSyncExternalStore(
    subscribe,
    () => getUnpublishedEdits(cycleId),
    () => null,
  );
}

/** A stable setter bound to one schedule — the heatmap's
 *  `onUnpublishedCountChange` (it runs in an effect keyed on the callback,
 *  so the callback must not change identity between renders). */
export function useSetUnpublishedEdits(cycleId: string): (count: number) => void {
  return useCallback((count: number) => setUnpublishedEdits(cycleId, count), [cycleId]);
}

/** Tests only — forget every reported count. */
export function resetUnpublishedEdits(): void {
  counts.clear();
  emit();
}
